// src/components/CenterfyTracking.jsx
"use client";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useCenterfyTracking } from "../hooks/useCenterfyTracking";
import { trackPageView as trackGAPageView } from "../utils/analytics";

/**
 * CenterfyTracking Component
 * Wrapper component that initializes tracking and provides context
 * Must be placed inside BrowserRouter to work properly
 */
export default function CenterfyTracking({ children }) {
  const location = useLocation();
  const { trackPageView } = useCenterfyTracking();

  // Track Google Analytics page view on route change
  useEffect(() => {
    // Get page title from document or use pathname as fallback
    const pageTitle = document.title || location.pathname;

    // Track page view in Google Analytics
    trackGAPageView(location.pathname + location.search, pageTitle);
  }, [location.pathname, location.search]);

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Re-track page view when user returns to tab
        trackPageView(location.pathname, {
          visibilityChange: true,
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [trackPageView, location.pathname]);

  // Handle beforeunload (optional - for final page view tracking)
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Track final page view before leaving
      // Note: This may not always fire due to browser restrictions
      trackPageView(location.pathname, {
        beforeUnload: true,
      });
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [trackPageView, location.pathname]);

  // Render children (the app)
  return <>{children}</>;
}
